import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { formatUSD, resolveImage } from "@/lib/api";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { useCart } from "@/lib/cart";
import { toast } from "sonner";
import { Star, Minus, Plus, ShoppingBag, Truck, Leaf, Award } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const NO_SIDE = "__none__";

export default function Product() {
  const { slug } = useParams();
  const [product, setProduct] = useState(null);
  const [qty, setQty] = useState(1);
  const [selectedOptions, setSelectedOptions] = useState({});
  const [sideCatalog, setSideCatalog] = useState([]); // [{name, price}]
  const [activeImageIdx, setActiveImageIdx] = useState(0);
  const { addItem } = useCart();

  useEffect(() => {
    api.get(`/products/${slug}`).then((r) => {
      setProduct(r.data);
      const initOpts = {};
      Object.entries(r.data.options || {}).forEach(([k, v]) => {
        initOpts[k] = v[0];
      });
      setSelectedOptions(initOpts);
    });
    api.get("/sides").then((r) => setSideCatalog(r.data?.sides || [])).catch(() => {});
  }, [slug]);

  if (!product) {
    return (
      <div className="bg-white text-[var(--dsf-text)] min-h-screen">
        <Header />
        <div className="pt-32 text-center text-[var(--dsf-text-muted)]">Loading…</div>
      </div>
    );
  }

  // Side surcharge from global catalog
  const sidePrice = (sideName) => {
    if (!sideName || sideName === NO_SIDE) return 0;
    const found = sideCatalog.find((s) => s.name.toLowerCase() === String(sideName).toLowerCase());
    return found?.price || 0;
  };

  const surcharge = Object.entries(selectedOptions).reduce((sum, [k, v]) => {
    if (k.toLowerCase() === "side") return sum + sidePrice(v);
    return sum;
  }, 0);

  const finalPrice = (product.price + surcharge) * qty;

  const handleAdd = () => {
    // Strip placeholder "None" before saving to cart
    const opts = { ...selectedOptions };
    Object.keys(opts).forEach((k) => { if (opts[k] === NO_SIDE) opts[k] = ""; });
    addItem(product, qty, opts);
    toast.success(`${product.name} added to cart`, { description: `${qty} × ${formatUSD(product.price + surcharge)}` });
  };

  return (
    <div className="bg-white text-[var(--dsf-text)] min-h-screen">
      <Header />
      <section className="pt-28 pb-20 px-6 md:px-12 lg:px-20">
        <div className="max-w-6xl mx-auto">
          <Link to={`/univers/${product.universe || (product.universes && product.universes[0])}`} className="text-xs uppercase tracking-[0.22em] text-[var(--dsf-text-muted)] hover:text-[var(--dd-green)] transition">
            ← Back
          </Link>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 mt-6">
            <div>
              {(() => {
                const imgs = [product.image, ...(Array.isArray(product.gallery) ? product.gallery : [])].filter(Boolean);
                const uniqImgs = Array.from(new Set(imgs));
                const main = uniqImgs[activeImageIdx] || uniqImgs[0] || product.image;
                return (
                  <div className="space-y-3" data-testid="product-image-gallery">
                    <div className="relative">
                      <img src={resolveImage(main)} alt={product.name} className="w-full aspect-[4/5] object-cover rounded-2xl" data-testid="product-image" />
                      {product.available === false && (
                        <div className="absolute inset-0 flex items-center justify-center bg-black/40 rounded-2xl">
                          <span className="bg-[var(--dd-coral)] text-white text-sm font-bold uppercase tracking-[0.22em] px-5 py-2 rounded-full shadow-lg">Sold out</span>
                        </div>
                      )}
                    </div>
                    {uniqImgs.length > 1 && (
                      <div className="grid grid-cols-5 gap-2" data-testid="product-thumbs">
                        {uniqImgs.slice(0, 8).map((src, i) => (
                          <button
                            key={src + i}
                            type="button"
                            onClick={() => setActiveImageIdx(i)}
                            className={`aspect-square overflow-hidden rounded-lg border-2 transition ${i === activeImageIdx ? "border-[var(--dd-green)]" : "border-transparent hover:border-[var(--dd-bone-dark)]"}`}
                            data-testid={`product-thumb-${i}`}
                          >
                            <img src={resolveImage(src)} alt={`${product.name} ${i + 1}`} className="w-full h-full object-cover" />
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>
            <div>
              <div className="flex items-center gap-1.5 text-[var(--dd-mustard)] mb-3">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Star key={i} size={14} fill={i < Math.floor(product.rating || 0) ? "currentColor" : "transparent"} />
                ))}
                <span className="text-xs text-[var(--dsf-text-muted)] ml-1">{product.reviews_count || 0} reviews</span>
              </div>
              <h1 className="font-serif text-4xl md:text-5xl text-[var(--dd-green)] mb-4">{product.name}</h1>
              <div className="text-3xl font-serif text-[var(--dd-green)] mb-6" data-testid="product-price">{formatUSD(product.price)}</div>
              <p className="text-[var(--dsf-text-secondary)] leading-relaxed mb-8" data-testid="product-description">{product.description}</p>

              {/* Options */}
              {Object.entries(product.options || {}).map(([key, values]) => {
                const isSide = key.toLowerCase() === "side";
                return (
                  <div key={key} className="mb-6" data-testid={`option-group-${key}`}>
                    <div className="text-[11px] uppercase tracking-[0.22em] text-[var(--dsf-text-secondary)] mb-3">
                      {key} {isSide && <span className="text-[var(--dsf-text-muted)] normal-case tracking-normal">(optional)</span>}
                    </div>
                    <div className="flex gap-2 flex-wrap">
                      {isSide && (
                        <button
                          onClick={() => setSelectedOptions((p) => ({ ...p, [key]: NO_SIDE }))}
                          className={`px-4 py-2 text-sm border transition-colors ${selectedOptions[key] === NO_SIDE
                            ? "border-[var(--dd-green)] bg-[var(--dd-green)] text-white"
                            : "border-black/10 text-[var(--dd-green)] hover:border-[var(--dd-green)]"}`}
                          data-testid={`option-${key}-none`}
                        >
                          No side
                        </button>
                      )}
                      {values.map((v) => {
                        const extra = isSide ? sidePrice(v) : 0;
                        return (
                          <button
                            key={v}
                            onClick={() => setSelectedOptions((p) => ({ ...p, [key]: v }))}
                            className={`px-4 py-2 text-sm border transition-colors ${selectedOptions[key] === v
                              ? "border-[var(--dd-green)] bg-[var(--dd-bone)] text-[var(--dd-green)] font-semibold"
                              : "border-black/10 text-[var(--dd-green)] hover:border-[var(--dd-green)]"}`}
                            data-testid={`option-${key}-${v}`}
                          >
                            {v}{extra > 0 && <span className="ml-1.5 text-[var(--dd-coral)] font-semibold">+{formatUSD(extra)}</span>}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })}

              {/* Quantity & Add to cart */}
              <div className="flex flex-col sm:flex-row gap-3 mb-8">
                <div className="flex items-center border border-black/10 rounded-xl">
                  <button onClick={() => setQty(Math.max(1, qty - 1))} className="px-4 py-3" data-testid="qty-decrease">
                    <Minus size={14} />
                  </button>
                  <span className="px-6 font-medium" data-testid="qty-value">{qty}</span>
                  <button onClick={() => setQty(qty + 1)} className="px-4 py-3" data-testid="qty-increase">
                    <Plus size={14} />
                  </button>
                </div>
                <button
                  onClick={handleAdd}
                  className="btn-primary flex-1 justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="add-to-cart-button"
                  disabled={product.available === false}
                >
                  <ShoppingBag size={16} /> {product.available === false ? "Sold out" : `Add to cart — ${formatUSD(finalPrice)}`}
                </button>
              </div>

              <div className="text-xs text-[var(--dsf-text-muted)]">
                {product.available === false ? (
                  <span className="text-[var(--dd-coral)] font-medium" data-testid="product-unavailable-msg">This item is currently unavailable.</span>
                ) : product.stock < 20 ? (
                  <span className="text-[var(--dsf-orange)] font-medium">Only {product.stock} left — order fast</span>
                ) : (
                  <span>In stock · ready for pickup</span>
                )}
              </div>

              {/* Trust */}
              <div className="grid grid-cols-3 gap-4 mt-8 pt-8 border-t border-black/5">
                {[
                  { icon: Truck, label: "Fast pickup" },
                  { icon: Leaf, label: "Fresh ingredients" },
                  { icon: Award, label: "Made in Houston" },
                ].map((it, i) => (
                  <div key={i} className="text-center">
                    <it.icon size={20} className="mx-auto text-[var(--dsf-orange)] mb-2" />
                    <div className="text-[11px] uppercase tracking-[0.18em] text-[var(--dsf-text-secondary)]">{it.label}</div>
                  </div>
                ))}
              </div>

              {/* Accordion */}
              <Accordion type="single" collapsible className="mt-10">
                <AccordionItem value="ingredients">
                  <AccordionTrigger className="font-serif text-lg">Ingredients & origin</AccordionTrigger>
                  <AccordionContent className="text-[var(--dsf-text-secondary)] leading-relaxed">
                    Ingredients sourced directly from our trusted partners. Slow cooked, traditional Senegalese recipes revisited by our chef.
                  </AccordionContent>
                </AccordionItem>
                <AccordionItem value="reviews">
                  <AccordionTrigger className="font-serif text-lg">Customer reviews ({product.reviews_count || 0})</AccordionTrigger>
                  <AccordionContent>
                    <div className="space-y-4">
                      <div className="text-sm text-[var(--dsf-text-muted)] italic">No reviews loaded for this product yet. Visit the homepage to share your experience.</div>
                    </div>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </div>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
}
